import React from 'react';
import { parseCheckAcknowledgements, parseInvoiceChecks, unacknowledgedWarnings } from './incomingInvoiceChecks';
import { incomingReviewAttention } from './incomingReviewAttention';
import { todayYMD } from '../../../utils/date';
import { ModuleLink } from '../../../components/ModuleLink';
import { contactDisplayName } from '../../../types';
import { contactDetailPath, organizationDetailPath } from '../../../utils/contactRoutes';
import type { InvoiceTab } from '../../../utils/invoiceRoutes';
import type { InvoicePartyTarget } from './invoicePartyDisplay';
import {
  INVOICE_OVERHEAD_ALLOCATION,
  incomingVendorNumberDuplicateMessage,
  invoiceAuftragDisplayLabel,
  invoiceAuftragPatchFromSelect,
  invoiceAuftragSelectValue,
  type IncomingInvoiceDuplicateOf,
} from './incomingInvoice';
import type { ContactOption, InvoiceDocument, InvoiceStatus, LeadOption, OrganizationOption } from '../types';

export function leadDisplayLabel(lead: LeadOption): string {
  const title = (lead.lead_title || '').trim();
  const displayId = String(lead.display_id || lead.id || '').trim();
  const name = (lead.name || '').trim();
  return [displayId, title || name].filter(Boolean).join(' · ') || 'Projekt';
}

export function contactOptionLabel(contact: ContactOption): string {
  const name = contactDisplayName(contact) || contact.company || contact.name || 'Kontakt';
  return `${name}${contact.email ? ` · ${contact.email}` : ''}`;
}

export function isLieferantOrganization(org: OrganizationOption): boolean {
  return !org.organization_type || org.organization_type === 'Lieferant';
}

export function supplierOrganizations(organizations: OrganizationOption[], currentId?: string | null): OrganizationOption[] {
  const current = String(currentId ?? '').trim();
  return organizations.filter((org) => isLieferantOrganization(org) || org.id === current);
}

export function displayOrDash(value: string): string {
  return value.trim() || '—';
}

export function incomingAuftragSelectOptions(leads: LeadOption[]): Array<{ value: string; label: string }> {
  return [
    { value: '', label: 'Kein Auftrag' },
    { value: INVOICE_OVERHEAD_ALLOCATION, label: 'Gemeinkosten' },
    ...leads.map((lead) => ({ value: String(lead.id), label: leadDisplayLabel(lead) })),
  ];
}

export function outgoingAuftragSelectOptions(leads: LeadOption[]): Array<{ value: string; label: string }> {
  return [
    { value: '', label: 'Kein Auftrag' },
    ...leads.map((lead) => ({ value: String(lead.id), label: leadDisplayLabel(lead) })),
  ];
}

export { invoiceAuftragDisplayLabel, invoiceAuftragPatchFromSelect, invoiceAuftragSelectValue, INVOICE_OVERHEAD_ALLOCATION };

export const STATUS_OPTIONS: Array<{ id: Exclude<InvoiceStatus, 'all'>; label: string }> = [
  { id: 'draft', label: 'Prüfung' },
  { id: 'open', label: 'Offen' },
  { id: 'overdue', label: 'Überfällig' },
  { id: 'extraction_failed', label: 'Fehler' },
  { id: 'paid', label: 'Bezahlt' },
  { id: 'rejected', label: 'Abgelehnt' },
  { id: 'cancelled', label: 'Storniert' },
];

export const STATUS_LABEL: Record<Exclude<InvoiceStatus, 'all'>, string> = {
  active: 'Aktiv',
  open: 'Offen',
  paid: 'Bezahlt',
  overdue: 'Überfällig',
  draft: 'Entwurf',
  extraction_pending: 'Offen',
  extraction_failed: 'Fehler',
  rejected: 'Abgelehnt',
  cancelled: 'Storniert',
};

/** Compact status pill colors for lists (incoming Prüfung = amber). */
export function invoiceStatusBadgeClass(status: string | null | undefined): string {
  const raw = String(status ?? '').trim().toLowerCase();
  if (raw === 'paid') return 'bg-cyan-50 text-cyan-700 border-cyan-200';
  if (raw === 'cancelled') return 'bg-app-surface-muted text-app-text-muted border-app-border';
  if (raw === 'overdue' || raw === 'extraction_failed' || raw === 'rejected') {
    return 'bg-rose-50 text-rose-700 border-rose-200';
  }
  if (raw === 'draft' || raw === 'extraction_pending') {
    return 'bg-amber-50 text-amber-800 border-amber-200';
  }
  return 'bg-blue-50 text-blue-700 border-blue-200';
}

export function statusDisplayLabel(
  status: InvoiceStatus,
  tab: InvoiceTab,
  row?: Pick<InvoiceDocument, 'document_type' | 'rejection_reason'> | null,
): string {
  if (status === 'all') return 'Alle';
  if (status === 'rejected' && row?.rejection_reason === 'not_invoice') return 'Keine Rechnung';
  if (status === 'rejected' && row?.rejection_reason === 'reclassified') return 'Umgewandelt';
  if (tab === 'quotes' && status === 'paid') return 'Angenommen';
  if (tab === 'incoming' && (status === 'draft' || status === 'extraction_pending')) return 'Prüfung';
  if (row?.document_type === 'invoice_correction' && status === 'paid') return 'Ausbezahlt';
  if (row?.document_type === 'incoming_invoice_correction' && status === 'paid') return 'Verrechnet';
  if (row?.document_type === 'credit_note' && status === 'paid') return 'Verschickt';
  if (status === 'cancelled') return 'Storniert';
  return STATUS_LABEL[status];
}

/** Status shown in dropdowns. Incoming extract stays in Prüfung; never map to Offen. */
export function invoiceStatusSelectValue(
  status: string,
  tab: InvoiceTab,
): Exclude<InvoiceStatus, 'all'> {
  if (status === 'extraction_pending') {
    return tab === 'incoming' ? 'draft' : 'open';
  }
  return status as Exclude<InvoiceStatus, 'all'>;
}

export function isInvoiceStatusLockedDuringExtraction(status: string | null | undefined): boolean {
  return String(status ?? '').trim() === 'extraction_pending';
}

export function statusOptionsForTab(tab: InvoiceTab) {
  return STATUS_OPTIONS.filter((option) => {
    if (option.id === 'draft') return tab === 'incoming';
    if (option.id === 'rejected') return tab === 'quotes' || tab === 'incoming';
    if (option.id === 'cancelled') return tab === 'outgoing' || tab === 'incoming';
    return true;
  });
}

/**
 * Status-Auswahl im Detail: Eingangsrechnungen werden nur über „Freigeben“ gebucht.
 * In Prüfung: Prüfung oder Abgelehnt. Freigegeben: Buchungsstatus, zurück in Prüfung nur per Rücknahme.
 */
export function statusOptionsForRow(tab: InvoiceTab, row: Pick<InvoiceDocument, 'status' | 'approved_at'>) {
  const options = statusOptionsForTab(tab);
  if (tab !== 'incoming') return options;
  const booked = ['open', 'overdue', 'paid'];
  if (!row.approved_at && !booked.includes(row.status)) {
    return options.filter((option) => option.id === 'draft' || option.id === 'rejected' || option.id === row.status);
  }
  return options.filter((option) => option.id !== 'draft');
}

export function getDisplayId(row: InvoiceDocument): string {
  return row.invoice_number || row.reference_id || row.filename || row.id.slice(0, 8);
}

export function dateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

export function invoicePartyHref(target: InvoicePartyTarget | null): string | null {
  if (!target) return null;
  return target.kind === 'organization'
    ? organizationDetailPath(target.id)
    : contactDetailPath(target.id);
}

const INVOICE_ENTITY_LINK_CLASS = 'text-app-text hover:text-pipedrive-blue hover:underline';

export function InvoiceEntityLink({
  to,
  title,
  className,
  children,
}: {
  to: string;
  title: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <ModuleLink
      to={to}
      onClick={(event) => event.stopPropagation()}
      className={`${INVOICE_ENTITY_LINK_CLASS} ${className ?? ''}`}
      title={title}
    >
      {children}
    </ModuleLink>
  );
}

export function IncomingInvoiceDuplicateChip({ className }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800 ${className ?? ''}`}
    >
      Duplikat
    </span>
  );
}

/** Liste: nur Ausnahmen einer Eingangsrechnung in Prüfung (Fehler, offene Hinweise, zweite Freigabe). */
export function IncomingInvoiceReviewChip({
  row,
  className,
}: {
  row: Pick<InvoiceDocument, 'status' | 'approved_at' | 'approval_first_by' | 'checks' | 'checks_acknowledged'>;
  className?: string;
}) {
  if (row.approved_at || row.status !== 'draft') return null;
  const checks = parseInvoiceChecks(row.checks);
  const errors = checks.filter((check) => check.severity === 'error').length;
  const open = unacknowledgedWarnings(checks, parseCheckAcknowledgements(row.checks_acknowledged));
  const highlight = open.some((check) => check.highlight);
  const base = 'inline-flex items-center rounded-full border px-1.5 py-0.5 text-xs font-medium';
  if (errors > 0) {
    return (
      <span className={`${base} border-rose-200 bg-rose-50 text-rose-700 ${className ?? ''}`} title="Die Prüfung hat Fehler gefunden.">
        {errors === 1 ? '1 Fehler' : `${errors} Fehler`}
      </span>
    );
  }
  if (open.length > 0) {
    return (
      <span
        className={`${base} ${highlight ? 'border-amber-400 bg-amber-100 text-amber-900' : 'border-amber-200 bg-amber-50 text-amber-800'} ${className ?? ''}`}
        title={open.map((check) => check.message).join(' · ')}
      >
        {open.length === 1 ? '1 Hinweis' : `${open.length} Hinweise`}
      </span>
    );
  }
  if (row.approval_first_by) {
    return <span className={`${base} border-app-border bg-app-surface-muted text-app-text-muted ${className ?? ''}`}>2. Freigabe</span>;
  }
  // Nichts zu tun: kein Chip. Die Statusspalte zeigt „Prüfung“ bereits.
  return null;
}

/** Liste: Frist einer Eingangsrechnung in Prüfung (Skonto, Fälligkeit) oder „liegt seit … Tagen“. */
export function IncomingReviewAttentionChip({
  row,
  staleDays,
  className,
}: {
  row: Pick<InvoiceDocument, 'status' | 'approved_at' | 'source' | 'skonto_amount' | 'skonto_due_date' | 'due_date' | 'created_at'>;
  staleDays: number;
  className?: string;
}) {
  const attention = incomingReviewAttention(row, { today: todayYMD(), staleDays });
  if (!attention) return null;
  const tone = attention.kind === 'stale'
    ? 'border-app-border bg-app-surface-muted text-app-text-muted'
    : 'border-amber-200 bg-amber-50 text-amber-800';
  return (
    <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-xs font-medium ${tone} ${className ?? ''}`}>
      {attention.label}
    </span>
  );
}

export function IncomingCreditChip({ className }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-xs font-medium text-rose-700 ${className ?? ''}`}
    >
      Gutschrift
    </span>
  );
}

export function IncomingInvoiceDuplicateBanner({
  duplicateOf,
  onOpenExisting,
}: {
  duplicateOf: IncomingInvoiceDuplicateOf;
  onOpenExisting?: (invoiceId: string) => void;
}) {
  const canOpen = Boolean(duplicateOf.id && onOpenExisting);
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900">
      <p>
        {incomingVendorNumberDuplicateMessage(duplicateOf)}{' '}
        {duplicateOf.reason && duplicateOf.reason !== 'vendor_invoice_number'
          ? 'Dieser Beleg bleibt in Prüfung. Ist es keine Dublette, Status auf „Offen“ setzen.'
          : 'Dieser Beleg bleibt in Prüfung und wird nicht gebucht.'}
      </p>
      {canOpen ? (
        <button
          type="button"
          className="mt-1.5 font-medium text-amber-950 underline underline-offset-2 hover:no-underline"
          onClick={() => onOpenExisting?.(duplicateOf.id)}
        >
          Bestehende Rechnung öffnen
        </button>
      ) : null}
    </div>
  );
}
